"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle, MicOff, Mic } from "lucide-react";
import {
  ProbeMessage,
  ProbeRequest,
  ProbeResponse,
  ProbeResult,
} from "@/lib/types/probe.types";
import { useEnv } from "@/context/envContext";
import { AvatarQuality, VoiceEmotion } from "@heygen/streaming-avatar";
import { addAudioPath } from "@/lib/audio-storage";

// Add this near the top of the file with other interfaces
declare global {
  interface Window {
    microphoneStream?: MediaStream;
    AudioContext: typeof AudioContext;
    webkitAudioContext: typeof AudioContext;
  }
}

const CONSTANTS = {
  SILENCE_THRESHOLD: 4, // in decibels
  SILENCE_DURATION: 3000, // 3 seconds after user responds in milliseconds
  SESSION_WARNING_TIME: 12 * 60 * 1000, // 12 minutes in milliseconds
  SESSION_MAX_TIME: 15 * 60 * 1000, // 15 minutes in milliseconds (12 + 3)
  ANSWER_WINDOW_DURATION: 15 * 1000, // 15 seconds answer window in milliseconds
};

const sanitizeInput = (input: string): string => {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#x27;")
    .replace(/\//g, "&#x2F;");
};

const generateResponse = async (
  message: string,
  chatHistory: ProbeMessage[],
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">,
  apiUrl: string,
  csrfToken: string,
  sessionId: string,
  applicationId?: string,
  audioBlob?: Blob,
  interviewLinkId?: string
): Promise<ProbeResponse> => {
  try {
    // If audio blob is provided, use multipart form data
    if (audioBlob) {
      const formData = new FormData();
      
      // Add all fields from probeConfig
      Object.entries(probeConfig).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
        }
      });
      
      // Add required fields
      formData.append("message", message);
      formData.append("chat_history", JSON.stringify(chatHistory));
      if (applicationId) {
        formData.append("applicationId", applicationId);
      }
      
      // Add audio file
      const audioFile = new File([audioBlob], `audio-${Date.now()}.webm`, { 
        type: audioBlob.type || 'audio/webm' 
      });
      formData.append("audio", audioFile);
      
      const response = await fetch(`${apiUrl}/api/career/probe`, {
        method: "POST",
        headers: {
          // Don't set Content-Type - browser will set it with boundary for multipart
          "X-CSRF-Token": csrfToken,
          "X-Heygen-Session": sessionId || '',
          "X-Interview-Link-Id": interviewLinkId || "",
        },
        body: formData,
        credentials: "include", // Send session cookie
      });

      if (!response.ok) {
        throw new Error("Failed to generate response");
      }

      return response.json();
    } else {
      // No audio - use JSON
      const response = await fetch(`${apiUrl}/api/career/probe`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Heygen-Session": sessionId || ''
        },
        body: JSON.stringify({
          ...probeConfig,
          message,
          applicationId: applicationId,
          chat_history: JSON.stringify(chatHistory),
        }),
        credentials: "include", // Send session cookie
      });

      if (!response.ok) {
        throw new Error("Failed to generate response");
      }

      return response.json();
    }
  } catch (error) {
    console.error("Error generating response:", error);
    throw error;
  }
};

// Add IDB interface
interface SavedProbeData {
  jobId: string;
  chatHistory: ProbeMessage[];
  lastUpdated: string;
}

// Add IDB initialization
const initDB = async () => {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open("candidateInterviewDB", 1);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains("probes")) {
        db.createObjectStore("probes", { keyPath: "jobId" });
      }
    };
  });
};

// Add save and load functions
const saveToIDB = async (jobId: string, chatHistory: ProbeMessage[]) => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["probes"], "readwrite");
      const store = transaction.objectStore("probes");

      const saveData: SavedProbeData = {
        jobId,
        chatHistory,
        lastUpdated: new Date().toISOString(),
      };

      const request = store.put(saveData);

      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => resolve(true);
    });
  } catch (error) {
    console.error("Error saving probe data:", error);
  }
};

const loadFromIDB = async (jobId: string): Promise<SavedProbeData | null> => {
  try {
    const db = await initDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(["probes"], "readonly");
      const store = transaction.objectStore("probes");
      const request = store.get(jobId);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);
    });
  } catch (error) {
    console.error("Error loading probe data:", error);
    return null;
  }
};

const WelcomeScreenSkeleton = () => (
    <div className="min-h-screen flex items-center justify-center bg-background text-black p-4">
      <div className="max-w-2xl w-full space-y-8">
        <div className="text-center space-y-6">
          {/* Animated brain icon using border and pseudo-elements */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 border-4 border-[#164a7a] rounded-full animate-[spin_3s_linear_infinite]" />
            <div className="absolute inset-2 border-4 border-[#164a7a] rounded-full animate-[spin_2s_linear_infinite_reverse]" />
            <div className="absolute inset-4 border-4 border-[#164a7a] rounded-full animate-[spin_4s_linear_infinite]" />
          </div>
  
          <h2 className="text-2xl font-semibold text-[#164a7a]">
            Preparing Your Interview
          </h2>
  
          {/* Animated dots */}
          <div className="flex justify-center items-center gap-2">
            <div
              className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
              style={{ animationDelay: "0s" }}
            />
            <div
              className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
              style={{ animationDelay: "0.2s" }}
            />
            <div
              className="w-2 h-2 bg-[#164a7a] rounded-full animate-[bounce_1s_infinite]"
              style={{ animationDelay: "0.4s" }}
            />
          </div>
  
          <div className="space-y-4 text-center max-w-md mx-auto">
            <p className="text-muted-foreground">
              Our AI interviewer is crafting personalized questions to understand
              your experience and potential.
            </p>
            <p className="text-sm text-muted-foreground">
              This may take a few moments...
            </p>
          </div>
        </div>
      </div>
    </div>
);
  

export function CandidateInterviewComponent({
  probeConfig,
  jobId,
  onComplete,
  csrfToken,
  applicationId,
  customHeygenApiKey,
  interviewLinkId,
}: {
  probeConfig: Omit<ProbeRequest, "message" | "chat_history">;
  jobId: string;
  previewMode?: boolean;
  onComplete?: (result: ProbeResult) => void;
  csrfToken: string;
  applicationId?: string;
  customHeygenApiKey?: string;
  interviewLinkId?: string;
}) {
  const env = useEnv();
  const [chatHistory, setChatHistory] = useState<ProbeMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [textInput, setTextInput] = useState("");
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [showingPreviousMessages, setShowingPreviousMessages] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isTypingComplete, setIsTypingComplete] = useState(true);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [progress, setProgress] = useState(0);
  // Add a ref to track if onComplete has been called
  const onCompleteCalledRef = useRef(false);
  const [microphoneActive, setMicrophoneActive] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  
  // Add refs and state for Heygen avatar
  const streamingAvatarRef = useRef<any>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [avatarReady, setAvatarReady] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);

  // Add these refs and states near your other state declarations
  const streamRef = useRef<MediaStream | null>(null);
  const recordingStateRef = useRef(false);
  const [currentTranscription, setCurrentTranscription] = useState("");
  
  // Audio chunk accumulation for combining before sending
  const accumulatedAudioChunksRef = useRef<Blob[]>([]);
  const [combinedAudioBlob, setCombinedAudioBlob] = useState<Blob | undefined>(undefined);
  
  // Full interview recording (mic + speaker)
  const fullInterviewRecorderRef = useRef<MediaRecorder | null>(null);
  const fullInterviewAudioChunksRef = useRef<Blob[]>([]);
  const fullInterviewAudioContextRef = useRef<AudioContext | null>(null);
  const fullInterviewMediaStreamRef = useRef<MediaStream | null>(null);
  const fullInterviewDestinationRef = useRef<MediaStreamAudioDestinationNode | null>(null);
  const fullInterviewBlobRef = useRef<Blob | null>(null); // Store the final full interview blob

  // Add a new state to track answer submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add this to your state declarations
  const [showApiKeyField, setShowApiKeyField] = useState(false);
  const [localApiKey, setLocalApiKey] = useState(customHeygenApiKey || '');

  // Add this state to track if we're in the preparation phase
  const [isPreparing, setIsPreparing] = useState(true);

  // Add this state to control transcription box visibility
  const [showTranscriptionBox, setShowTranscriptionBox] = useState(false);
  
  // Add auto/manual send mode state
  const [autoSendMode, setAutoSendMode] = useState<boolean>(true);
  const [isUserStoppedTalking, setIsUserStoppedTalking] = useState<boolean>(false);
  const [userTalkedAfterAI, setUserTalkedAfterAI] = useState<boolean>(false);
  const [lastAudioTime, setLastAudioTime] = useState<number>(0);
  const [silenceStartTime, setSilenceStartTime] = useState<number>(0);
  const [silenceCountdown, setSilenceCountdown] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isAvatarSpeaking, setIsAvatarSpeaking] = useState<boolean>(false);

  // Session timer states
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [sessionElapsedTime, setSessionElapsedTime] = useState<number>(0);
  const [showTimeWarning, setShowTimeWarning] = useState<boolean>(false);
  const [hasShownWarning, setHasShownWarning] = useState<boolean>(false);
  
  // Answer window timer states
  const [answerWindowActive, setAnswerWindowActive] = useState<boolean>(false);
  const [answerWindowRemaining, setAnswerWindowRemaining] = useState<number>(0);
  const answerWindowStartTimeRef = useRef<number | null>(null);

  // Update the useEffect to sync localApiKey when customHeygenApiKey changes
  useEffect(() => {
    setLocalApiKey(customHeygenApiKey || '');
  }, [customHeygenApiKey]);

  // Add toggle mute function
  const toggleMute = useCallback((): void => {
    setIsMuted((m) => !m);
  }, []);

  // Add refs to store current values to prevent infinite re-renders
  const isListeningRef = useRef(isListening);
  const isMutedRef = useRef(isMuted);
  const userTalkedAfterAIRef = useRef(userTalkedAfterAI);
  const silenceStartTimeRef = useRef(silenceStartTime);
  const isUserStoppedTalkingRef = useRef(isUserStoppedTalking);
  const lastAudioTimeRef = useRef(lastAudioTime);
  const isAvatarSpeakingRef = useRef(isAvatarSpeaking);

  // Update refs when state changes
  useEffect(() => {
    isListeningRef.current = isListening;
    isMutedRef.current = isMuted;
    userTalkedAfterAIRef.current = userTalkedAfterAI;
    silenceStartTimeRef.current = silenceStartTime;
    isUserStoppedTalkingRef.current = isUserStoppedTalking;
    lastAudioTimeRef.current = lastAudioTime;
    isAvatarSpeakingRef.current = isAvatarSpeaking;
  }, [isListening, isMuted, userTalkedAfterAI, silenceStartTime, isUserStoppedTalking, lastAudioTime, isAvatarSpeaking]);

  // Add dedicated recording audio level detection similar to audio-interview.tsx
  const setupRecordingAudioLevelDetection = useCallback((stream: MediaStream) => {
    try {
      const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioCtx();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      
      const loop = () => {
        // Use refs to get current values without causing re-renders
        // Stop processing only when not listening or avatar is speaking (not when muted)
        if (!isListeningRef.current || !recordingStateRef.current || isAvatarSpeakingRef.current) {
          // Continue loop but don't process audio when not listening or avatar speaking
          if (isListeningRef.current && recordingStateRef.current) {
            requestAnimationFrame(loop);
          }
          return;
        }
        
        analyser.getByteTimeDomainData(dataArray);
        let sum = 0; let max = 0;
        for (let i = 0; i < bufferLength; i++) {
          const v = Math.abs(dataArray[i] - 128);
          sum += v; max = Math.max(max, v);
        }
        const avg = sum / bufferLength;
        const scaled = Math.min(100, Math.max(0, (avg * 2) + (max / 2)));
        
        // Update audio level without causing re-renders
        setAudioLevel((prev) => {
          // If muted, treat as silent but continue silence detection
          const effectiveLevel = isMutedRef.current ? 0 : Math.round((prev * 0.3) + (scaled * 0.7));
          
          // Silence detection logic using refs to prevent re-renders
          const now = Date.now();
          const SILENCE_THRESHOLD = 4;
          
          // Use actual audio level for detection, but if muted, treat as silent
          const detectionLevel = isMutedRef.current ? 0 : effectiveLevel;
          
          if (detectionLevel > SILENCE_THRESHOLD && !isMutedRef.current) {
            // User is speaking (and not muted)
            setLastAudioTime(now);
            lastAudioTimeRef.current = now;
            setIsUserStoppedTalking(false);
            isUserStoppedTalkingRef.current = false;
            setUserTalkedAfterAI(true);
            userTalkedAfterAIRef.current = true;
            setSilenceStartTime(0);
            silenceStartTimeRef.current = 0;
            setSilenceCountdown(0);
            
          } else {
            // User is silent (or muted)
            if (userTalkedAfterAIRef.current) {
              // User has talked after AI - track for visual countdown and auto-submission
              if (silenceStartTimeRef.current === 0) {
                setSilenceStartTime(now);
                silenceStartTimeRef.current = now;
              } else {
                const silenceDuration = now - silenceStartTimeRef.current;
                if (silenceDuration >= 3000 && !isUserStoppedTalkingRef.current) {
                  setIsUserStoppedTalking(true);
                  isUserStoppedTalkingRef.current = true;
                }
              }
            }
          }
          
          return effectiveLevel;
        });
        
        // Continue loop
        if (isListeningRef.current && recordingStateRef.current) {
          requestAnimationFrame(loop);
        }
      };
      
      // Start the loop
      loop();
      
      // Return cleanup function
      return () => {
        if (audioContext.state !== 'closed') {
          audioContext.close();
        }
      };
    } catch (err) {
      console.error("Error setting up recording audio level detection:", err);
      return () => {}; // Return empty cleanup function
    }
  }, []); // Empty dependency array since we use refs

  // Modify this function to handle key combinations for both API key field and transcription box
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Show API key field when Ctrl+Shift+A is pressed
    if (e.ctrlKey && e.shiftKey && e.key === 'A') {
      setShowApiKeyField(prev => !prev);
      // Also toggle transcription box visibility with the same shortcut
      setShowTranscriptionBox(prev => !prev);
    }
  }, []);

  // Add this useEffect to listen for key combinations
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Set up video element when media stream changes
  useEffect(() => {
    if (mediaStream && videoRef.current) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(err => {
          console.error("Error playing video:", err);
        });
      };
      
      // If full interview recording is active, reconnect video audio
      if (fullInterviewRecorderRef.current && fullInterviewRecorderRef.current.state === 'recording' && fullInterviewAudioContextRef.current) {
        try {
          const videoAudioSource = fullInterviewAudioContextRef.current.createMediaStreamSource(mediaStream);
          if (fullInterviewDestinationRef.current) {
            videoAudioSource.connect(fullInterviewDestinationRef.current);
          }
        } catch (error) {
          console.warn('Could not reconnect video audio to recording:', error);
        }
      }
    }
  }, [mediaStream]);

  // Start avatar stream
  const startAvatarStream = useCallback(async () => {
    // Add a unique identifier for each call to track duplicates
    const callId = Date.now();
    
    try {
      // If already initializing, streaming, or initialized with session, don't start again
      if (isInitializing) {
        return;
      }
      
      if (isStreaming) {
        return;
      }
      
      if (isInitialized && sessionId) {
        return;
      }
      
      setIsInitializing(true);
      setIsLoading(true);
      setIsPreparing(true);
      
      // If not initialized yet, initialize the avatar first
      if (!isInitialized) {
        // Generate access token
        const response = await fetch(env.NEXT_PUBLIC_EXTERNAL_API_URL + `/api/heygen/generate-token`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              apiKey: localApiKey || '',
              csrfToken: csrfToken
            }),
          });

        if (!response.ok) {
          throw new Error('Failed to generate access token');
        }

        const tokenData = await response.json();
        
        // Initialize StreamingAvatar with the token
        if (typeof window !== 'undefined') {
          // Dynamically import StreamingAvatar
          const StreamingAvatar = (await import('@heygen/streaming-avatar')).default;
          
          streamingAvatarRef.current = new StreamingAvatar({
            token: tokenData.token
          });
          
          // Set up event listeners
          streamingAvatarRef.current.on('stream_ready', (event: { detail: MediaStream }) => {
            
            // Set the media stream
            if (event.detail) {
              setMediaStream(event.detail);
              setAvatarReady(true);
              setIsPreparing(false); // Avatar is ready, no longer preparing
            }
          });

          // Add avatar speaking detection events
          streamingAvatarRef.current.on('avatar_start_talking', () => {
            setIsAvatarSpeaking(true);
          });

          streamingAvatarRef.current.on('avatar_stop_talking', () => {
            setIsAvatarSpeaking(false);
            // Reset silence detection states when avatar finishes speaking
            setLastAudioTime(Date.now());
            setUserTalkedAfterAI(false);
            setSilenceStartTime(0);
            setSilenceCountdown(0);
            
            // Start 15-second answer window timer (only if not already active)
            if (!answerWindowStartTimeRef.current) {
              answerWindowStartTimeRef.current = Date.now();
              setAnswerWindowActive(true);
              setAnswerWindowRemaining(CONSTANTS.ANSWER_WINDOW_DURATION);
            }
          });
          
          setIsInitialized(true);
        }
      }
      
      // Only create and start avatar session if not already streaming
      if (!isStreaming && streamingAvatarRef.current) {
        const sessionData = await streamingAvatarRef.current.createStartAvatar({
          quality: AvatarQuality.High,
          avatarName: "June_HR_public",
          knowledgeId: "",
          voice: {
            voiceId: "68dedac41a9f46a6a4271a95c733823c",
            rate: 1.0,
            emotion: VoiceEmotion.SERIOUS,
          },
          language: "en",
          disableIdleTimeout: true,
        });

        setSessionId(sessionData.session_id);
        setIsStreaming(true);
        
        // Stop listening when avatar starts
        streamingAvatarRef.current.stopListening();
        setIsListening(false);
        
        // Set loading to false once avatar is ready
        // setIsLoading(false);
        // setIsPreparing(false);
      }
    } catch (error) {
      console.error(`[${callId}] Error starting avatar stream:`, error);
      setIsLoading(false); // Make sure to set loading to false in case of error
      setIsPreparing(false);
    } finally {
      setIsInitializing(false);
    }
  }, [isInitialized, isInitializing, isStreaming, env.NEXT_PUBLIC_EXTERNAL_API_URL, localApiKey, sessionId]);

  // Add a ref to track initialization
  const initializedRef = useRef(false);

  // Initialize component with fresh state (no saved data)
  useEffect(() => {
    const initializeFresh = async () => {
      // Skip if already initialized
      if (initializedRef.current) {
        return;
      }
      
      initializedRef.current = true;
      
      setIsLoading(true);
      setIsPreparing(true);
      
      try {
        // Clear any existing saved data first
        try {
          const db = await initDB();
          const transaction = db.transaction(["probes"], "readwrite");
          const store = transaction.objectStore("probes");
          await store.delete(jobId);
        } catch (error) {
          console.error("No existing data to clear or error clearing:", error);
        }
        
        // Always start with fresh state - no saved data
        setChatHistory([]);
        setCurrentQuestionIndex(-1); // Set to welcome screen
        setShowingPreviousMessages(false);
        setIsTypingComplete(true);
        
        // Only start preparing the avatar if it's not already initialized or in progress
        if (!isInitialized && !isInitializing && !isStreaming && !sessionId) {
          await startAvatarStream();
        }
      } catch (error) {
        console.error("Error initializing fresh interview:", error);
        setCurrentQuestionIndex(-1); // Set to welcome screen
      } finally {
        // Keep isLoading true until avatar is ready
        // We'll set it to false in the startAvatarStream function
        if (isInitialized || isStreaming) {
          setIsLoading(false);
          setIsPreparing(false);
        }
      }
    };

    initializeFresh();
  }, [jobId, isInitialized, isInitializing, isStreaming, startAvatarStream, sessionId]);

  // Start full interview recording (mic + speaker)
  const startFullInterviewRecording = useCallback(async () => {
    try {
      // Get microphone stream
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        } 
      });
      
      // Create audio context for mixing
      const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      const destination = audioContext.createMediaStreamDestination();
      
      // Connect microphone to destination
      const micSource = audioContext.createMediaStreamSource(micStream);
      micSource.connect(destination);
      
      // Connect video audio (avatar speech) to destination if available
      if (videoRef.current && mediaStream) {
        try {
          const videoAudioSource = audioContext.createMediaStreamSource(mediaStream);
          videoAudioSource.connect(destination);
        } catch (error) {
          console.warn('Could not connect video audio to recording:', error);
        }
      }
      
      // Create MediaRecorder for the mixed stream
      const mixedStream = destination.stream;
      const recorder = new MediaRecorder(mixedStream, { mimeType: 'audio/webm' });
      
      const chunks: Blob[] = [];
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        if (chunks.length > 0) {
          const fullRecordingBlob = new Blob(chunks, { type: 'audio/webm' });
          fullInterviewAudioChunksRef.current = [fullRecordingBlob];
          // Store the blob for later use in complete API
          fullInterviewBlobRef.current = fullRecordingBlob;
        }
        
        // Cleanup
        if (fullInterviewAudioContextRef.current) {
          fullInterviewAudioContextRef.current.close();
          fullInterviewAudioContextRef.current = null;
        }
        if (micStream) {
          micStream.getTracks().forEach(track => track.stop());
        }
      };
      
      recorder.start();
      
      // Store refs
      fullInterviewRecorderRef.current = recorder;
      fullInterviewAudioContextRef.current = audioContext;
      fullInterviewMediaStreamRef.current = micStream;
      fullInterviewDestinationRef.current = destination;
      
    } catch (error) {
      console.error('Error starting full interview recording:', error);
    }
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, interviewLinkId, jobId, mediaStream, probeConfig, sessionId]);
  
  // Stop full interview recording
  const stopFullInterviewRecording = useCallback(() => {
    if (fullInterviewRecorderRef.current && fullInterviewRecorderRef.current.state !== 'inactive') {
      fullInterviewRecorderRef.current.stop();
    }
  }, []);
  
  // Modify startInterview to check if avatar is already initialized
  const startInterview = useCallback(async () => {
    setIsLoading(true);
    setIsTypingComplete(false);
    setCurrentQuestionIndex(0);
    
    // Start session timer
    setSessionStartTime(Date.now());
    setSessionElapsedTime(0);
    setShowTimeWarning(false);
    setHasShownWarning(false);
    
    // Reset all states for fresh interview
    setChatHistory([]);
    setCurrentQuestionIndex(0);
    setIsUserStoppedTalking(false);
    setUserTalkedAfterAI(false);
    setLastAudioTime(Date.now());
    setSilenceStartTime(0);
    setSilenceCountdown(0);
    setTextInput("");
    setCurrentTranscription("");
    
    try {
      // If the avatar stream is not already started, start it now
      if (!isStreaming) {
        await startAvatarStream();
      }
      
      // Start full interview recording after avatar is ready
      if (mediaStream) {
        // Wait a bit for media stream to be fully ready
        setTimeout(() => {
          startFullInterviewRecording();
        }, 1000);
      }
      
      // Now generate the initial response
      if (sessionId) {
        const response = await generateResponse(
          "hello",
          [],
          probeConfig,
          env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
          csrfToken,
          sessionId,
          applicationId,
          undefined, // No audio for initial greeting
          interviewLinkId // Pass interview link ID
        );
        
        const durationMs = response.duration_ms;
        
        if (response.data.chat_history) {
          setChatHistory(response.data.chat_history);
          setCurrentQuestionIndex(0);
          setIsTypingComplete(false);
        }
        
        // After durationMs, start listening
        setTimeout(() => {
          if (streamingAvatarRef.current && isStreaming) {
            setIsListening(true);
            streamingAvatarRef.current.startListening();
            
            // Reset silence detection states when starting to listen
            setIsUserStoppedTalking(false);
            setUserTalkedAfterAI(false);
            setLastAudioTime(Date.now());
            setSilenceStartTime(0);
            setSilenceCountdown(0);
            
            // Make sure the video element is properly connected to the media stream
            if (videoRef.current && mediaStream) {
              videoRef.current.srcObject = mediaStream;
              videoRef.current.play().catch(err => {
                console.error("Error playing video after loading:", err);
              });
            }
          }
        }, durationMs);
      } else {
        console.error("startInterview: No sessionId available, cannot generate response");
      }
    } catch (error) {
      console.error("Error starting interview:", error);
    } finally {
      setIsLoading(false);
    }
  }, [probeConfig, env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, sessionId, isStreaming, startAvatarStream, mediaStream, isInitialized, isInitializing, startFullInterviewRecording]);

  // Modify the checkMicrophone function to not initialize the avatar
  const checkMicrophone = () => {
    // Request microphone access only (no video)
    if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
      navigator.mediaDevices.getUserMedia({ audio: true, video: false })
        .then((stream) => {
          setMicrophoneActive(true);
          
          try {
            // Set up audio context to analyze microphone input levels
            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            const audioSource = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            
            // Use a smaller FFT size for more responsive updates
            analyser.fftSize = 256;
            analyser.smoothingTimeConstant = 0.5;
            
            audioSource.connect(analyser);
            
            // Create a buffer to receive data from the analyser
            const bufferLength = analyser.frequencyBinCount;
            const dataArray = new Uint8Array(bufferLength);
            
            // Function to continuously update audio level (simplified - just for visual feedback)
            const updateAudioLevel = () => {
              // When muted, set audio level to 0 and don't process audio
              if (isMuted) {
                setAudioLevel(0);
                if (microphoneActive) {
                  requestAnimationFrame(updateAudioLevel);
                }
                return;
              }
              
              // Use getByteTimeDomainData for time domain analysis (waveform)
              analyser.getByteTimeDomainData(dataArray);
              
              // Calculate audio level from waveform data
              let sum = 0;
              let max = 0;
              
              for (let i = 0; i < bufferLength; i++) {
                // Convert to a value between -128 and 127
                const value = Math.abs(dataArray[i] - 128);
                sum += value;
                max = Math.max(max, value);
              }
              
              // Use both average and max for better sensitivity
              const average = sum / bufferLength;
              
              // Scale the level to make it more visible
              // Emphasize even small sounds
              const scaledLevel = Math.min(100, Math.max(0, (average * 2) + (max / 2)));
              
              // Update state with the new level (just for visual feedback)
              setAudioLevel(prev => {
                // Smooth transitions by averaging with previous value
                const currentLevel = Math.round((prev * 0.3) + (scaledLevel * 0.7));
                return currentLevel;
              });
              
              // Continue monitoring as long as microphone is active
              if (microphoneActive) {
                requestAnimationFrame(updateAudioLevel);
              }
            };
            
            // Start monitoring
            updateAudioLevel();            
          } catch (err) {
            console.error("Error setting up audio analyzer:", err);
          }
          
          // Store the stream reference to stop it later if needed
          window.microphoneStream = stream;
        })
        .catch((err) => {
          console.error("Error accessing microphone:", err);
          alert("Could not access your microphone. Please check your browser permissions and try again.");
        });
    } else {
      alert("Your browser doesn't support microphone access. Please try a different browser.");
    }
  };

  // Add this useEffect to clean up microphone resources when component unmounts
  useEffect(() => {
    return () => {
      // Clean up microphone stream when component unmounts
      if (window.microphoneStream) {
        window.microphoneStream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Add these functions to control the avatar stream
  const stopAvatarStream = useCallback(() => {
    // Stop the avatar stream
    if (streamingAvatarRef.current) {
      streamingAvatarRef.current.stopAvatar();
      setIsStreaming(false);
    }

    // Stop the transcription process
    recordingStateRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    setIsListening(false);
    setCurrentTranscription('');
    setTextInput('');
  }, []);

  

  // Add cleanup effect
  useEffect(() => {
    return () => {
      if (streamingAvatarRef.current) {
        streamingAvatarRef.current.stopAvatar();
      }
    };
  }, []);

  // Add this useEffect to ensure video is properly set up when loading ends
  useEffect(() => {
    if (!isLoading && mediaStream && videoRef.current) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.onloadedmetadata = () => {
        videoRef.current?.play().catch(err => {
          console.error("Error playing video after loading state change:", err);
        });
      };
    }
  }, [isLoading, mediaStream]);

  // Add this function to handle audio chunks
  const sendAudioChunk = async (audioBlob: Blob) => {
    if (!audioBlob || audioBlob.size <= 100) {
      return;
    }
    
    // Accumulate audio chunks for later combination (even if muted, for recording purposes)
    if (audioBlob && audioBlob.size > 100) {
      accumulatedAudioChunksRef.current.push(audioBlob);
    }
    
    // If muted, don't send for transcription but still accumulate the chunk
    if (isMuted) {
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append("audio", audioBlob, "audio.webm");
    //   formData.append("provider", "azure");
      
      // Add interview link ID if available (required for validation)
      if (interviewLinkId) {
        formData.append("interviewLinkId", interviewLinkId);
      }
      
      const response = await fetch(`${env.NEXT_PUBLIC_EXTERNAL_API_URL}/api/heygen/transcribe`, {
        method: "POST",
        body: formData,
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error("Server error:", errorText);
      } else {
        const data = await response.json();
        if (data.transcription) {
          // Update both currentTranscription and textInput with the new transcription
          const newTranscription = data.transcription.trim();
          setCurrentTranscription(prev => {
            const updated = prev ? `${prev} ${newTranscription}` : newTranscription;
            setTextInput(updated); // Keep textInput in sync with currentTranscription
            return updated;
          });
        }
      }
    } catch (error) {
      console.error("Error sending audio chunk:", error);
    }
  };

  // Add this function to handle continuous recording
  const startNewRecording = useCallback(() => {
    if (!streamRef.current || !recordingStateRef.current) return;
    
    const chunks: Blob[] = [];
    const options = { mimeType: 'audio/webm' };
    
    try {
      const recorder = new MediaRecorder(streamRef.current, options);
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = async () => {
        if (chunks.length > 0) {
          const audioBlob = new Blob(chunks, { type: 'audio/webm' });
          await sendAudioChunk(audioBlob);
        }
      };
      
      recorder.start();
      
      setTimeout(() => {
        if (recorder.state === "recording" && recordingStateRef.current) {
          recorder.stop();
          if (recordingStateRef.current) {
            startNewRecording();
          }
        }
      }, 3000); // 3 seconds per chunk; transcribe audio delay 3 seconds
      
    } catch (error) {
      console.error("Error creating new recorder:", error);
    }
  }, [env.NEXT_PUBLIC_EXTERNAL_API_URL]);

  // Add this useEffect to handle recording when isListening changes
  useEffect(() => {
    if (isListening) {
      // Start recording
      recordingStateRef.current = true;
      setCurrentTranscription("");
      setTextInput(""); // Reset text input when starting to listen
      // Clear accumulated audio chunks on fresh start
      accumulatedAudioChunksRef.current = [];
      setCombinedAudioBlob(undefined);
      
      if (!streamRef.current) {
        navigator.mediaDevices.getUserMedia({ 
          audio: {
            channelCount: 1,
            sampleRate: 16000,
            echoCancellation: true,
            noiseSuppression: true,
          } 
        })
        .then(stream => {
          streamRef.current = stream;
          // Set up dedicated audio level detection for the recording stream
          setupRecordingAudioLevelDetection(stream);
          startNewRecording();
        })
        .catch(error => {
          console.error("Error getting audio stream:", error);
          recordingStateRef.current = false;
        });
      } else {
        // Set up audio level detection for existing stream
        setupRecordingAudioLevelDetection(streamRef.current);
        startNewRecording();
      }
    } else {
      // Stop recording
      recordingStateRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
    
    return () => {
      recordingStateRef.current = false;
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [isListening, startNewRecording, setupRecordingAudioLevelDetection]);

  // Define handleAnswerSubmission function before it's used in useEffect
  const handleAnswerSubmission = useCallback(async (answer: string) => {
    // Make sure we're using the most up-to-date transcription
    const finalAnswer = answer.trim();
    
    if (!finalAnswer) {
      return;
    }
    
    const sanitizedAnswer = sanitizeInput(finalAnswer);

    setIsSubmitting(true);
    setShowingPreviousMessages(false);
    setIsTypingComplete(false); // Set to false as we're about to get a new response
    
    // Combine all accumulated audio chunks into one blob
    let combinedAudio: Blob | undefined = undefined;
    if (accumulatedAudioChunksRef.current.length > 0) {
      combinedAudio = new Blob(accumulatedAudioChunksRef.current, { type: 'audio/webm' });
      setCombinedAudioBlob(combinedAudio);
    }
    
    try {
      // Stop listening when submitting an answer
      if (streamingAvatarRef.current) {
        streamingAvatarRef.current.stopListening();
        setIsListening(false);
      }
      
      const response = await generateResponse(
        sanitizedAnswer,
        chatHistory,
        probeConfig,
        env.NEXT_PUBLIC_EXTERNAL_API_URL || "",
        csrfToken,
        sessionId || '',
        applicationId,
        combinedAudio, // Pass combined audio blob
        interviewLinkId // Pass interview link ID
      );
      
      // Save audio path to IndexedDB if present
      if (response?.audioPath && jobId) {
        try {
          await addAudioPath(jobId, response.audioPath);
        } catch (error) {
          console.error("Failed to save audio path to IndexedDB:", error);
        }
      }

      if (
        response.data.is_conversation_stop === "true" ||
        response.data.is_conversation_stop === "yes"
      ) {
        setChatHistory(response.data.chat_history);
        // Calculate progress based on number of questions
        // First 10 questions increase by larger amounts, after that smaller increments
        const questionCount = response.data.chat_history.length / 2; // Divide by 2 because each Q&A is 2 messages
        if (questionCount <= 10) {
          setProgress(Math.min(85, questionCount * 8)); // Each of first 10 questions adds 8%
        } else {
          // After 10 questions, divide remaining 15% among next questions
          const remainingProgress = Math.min(
            95 - 80,
            (questionCount - 10) * 1.5
          );
          setProgress(80 + remainingProgress);
        }
        
        // Add a delay before starting to listen again
        // This gives time for the avatar to speak the response
        setTimeout(() => {
          // Stop full interview recording before completing
          stopFullInterviewRecording();
          
        setIsComplete(true);
        stopAvatarStream();
        setProgress(100);

        // Start countdown from 3
        setCountdown(3);

        // Create countdown timer
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev === null || prev <= 1) {
              clearInterval(timer);
              // When countdown reaches 0, call onComplete only if it hasn't been called yet
              if (
                response.data.result &&
                onComplete &&
                !onCompleteCalledRef.current
              ) {
                // Set the flag to true to prevent multiple calls
                onCompleteCalledRef.current = true;
                // Pass the full interview blob along with the result
                onComplete({
                  ...response.data.result,
                  chat_history: response.data.chat_history,
                  fullInterviewBlob: fullInterviewBlobRef.current || undefined,
                } as any);
              }
              return null;
            }
            return prev - 1;
          });
        }, 1000);
        }, response.duration_ms || 3000); // Use the duration from the response or default to 3 seconds
        
      } else {
        setChatHistory(response.data.chat_history);
        // Calculate progress based on number of questions
        // First 10 questions increase by larger amounts, after that smaller increments
        const questionCount = response.data.chat_history.length / 2; // Divide by 2 because each Q&A is 2 messages
        if (questionCount <= 10) {
          setProgress(Math.min(85, questionCount * 8)); // Each of first 10 questions adds 8%
        } else {
          // After 10 questions, divide remaining 15% among next questions
          const remainingProgress = Math.min(
            95 - 80,
            (questionCount - 10) * 1.5
          );
          setProgress(80 + remainingProgress);
        }
        
        // Add a delay before starting to listen again
        // This gives time for the avatar to speak the response
        setTimeout(() => {
          if (streamingAvatarRef.current && !isComplete) {
            streamingAvatarRef.current.startListening();
            setIsListening(true);
            
            // Reset silence detection states when starting to listen
            setIsUserStoppedTalking(false);
            setUserTalkedAfterAI(false);
            setLastAudioTime(Date.now());
            setSilenceStartTime(0);
            setSilenceCountdown(0);
            
            // Reset answer window when starting to listen
            setAnswerWindowActive(false);
            answerWindowStartTimeRef.current = null;
          }
        }, response.duration_ms || 3000); // Use the duration from the response or default to 3 seconds
      }
    } catch (error) {
      console.error("Error handling answer submission:", error);
      setIsTypingComplete(true); // Ensure typing is complete if there's an error
      
      // If there's an error, still try to restart listening after a delay
      setTimeout(() => {
        if (streamingAvatarRef.current && !isComplete) {
          streamingAvatarRef.current.startListening();
          setIsListening(true);
          
          // Reset silence detection states when starting to listen
          setIsUserStoppedTalking(false);
          setUserTalkedAfterAI(false);
          setLastAudioTime(Date.now());
          setSilenceStartTime(0);
          setSilenceCountdown(0);
          
          // Reset answer window when starting to listen
          setAnswerWindowActive(false);
          answerWindowStartTimeRef.current = null;
        }
      }, 3000);
    } finally {
      setIsSubmitting(false); 
      setTextInput("");
      setCurrentTranscription("");
      // Clear accumulated audio chunks after submission
      accumulatedAudioChunksRef.current = [];
      setCombinedAudioBlob(undefined);
    }
  }, [chatHistory, probeConfig, env.NEXT_PUBLIC_EXTERNAL_API_URL, csrfToken, sessionId, isComplete, stopAvatarStream, onComplete, onCompleteCalledRef, jobId, interviewLinkId]);

  // Add countdown timer for auto mode (not affected by mute state)
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    
    if (autoSendMode && userTalkedAfterAI && isListening) {
      interval = setInterval(() => {
        const now = Date.now();
        const timeSinceLastAudio = now - lastAudioTime;
        const remaining = Math.max(0, 3000 - timeSinceLastAudio);
        setSilenceCountdown(Math.ceil(remaining / 1000));
        
        if (remaining <= 0 && !isUserStoppedTalking) {
          setSilenceCountdown(0);
          setIsUserStoppedTalking(true);
        }
      }, 100);
    } else {
      setSilenceCountdown(0);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoSendMode, userTalkedAfterAI, isListening, lastAudioTime, textInput, handleAnswerSubmission, isUserStoppedTalking]);

  // Auto submit when user stops talking in auto mode (not blocked by mute state)
  useEffect(() => {
    if (autoSendMode && isUserStoppedTalking && textInput && textInput.trim() && !isSubmitting) {
      handleAnswerSubmission(textInput);
    }
  }, [autoSendMode, isUserStoppedTalking, textInput, isSubmitting, handleAnswerSubmission]);
  
  // Answer window timer - 15 seconds for user to respond after avatar speaks
  // Timer keeps counting down regardless of whether user is speaking
  useEffect(() => {
    if (!answerWindowActive || !answerWindowStartTimeRef.current) return;
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - (answerWindowStartTimeRef.current || Date.now());
      const remaining = Math.max(0, CONSTANTS.ANSWER_WINDOW_DURATION - elapsed);
      setAnswerWindowRemaining(remaining);
      
      // When time runs out
      if (remaining <= 0) {
        clearInterval(interval);
        setAnswerWindowActive(false);
        answerWindowStartTimeRef.current = null; // Reset the ref
        
        // If user has started talking, submit what they said
        if (currentTranscription || textInput) {
          setIsListening(false);
          setTimeout(() => {
            handleAnswerSubmission(textInput || currentTranscription);
          }, 500);
        } else {
          // No response at all - submit empty or skip
          setIsListening(false);
        }
      }
    }, 100); // Update every 100ms for smooth countdown
    
    return () => clearInterval(interval);
  }, [answerWindowActive, currentTranscription, textInput, handleAnswerSubmission]);
  
  // Stop answer window timer only when submitting (not when user talks)
  useEffect(() => {
    if (isSubmitting && answerWindowActive) {
      setAnswerWindowActive(false);
      answerWindowStartTimeRef.current = null; // Reset the ref for next question
    }
  }, [isSubmitting, answerWindowActive]);

  // Disabled IDB saving - always start fresh
  // useEffect(() => {
  //   if (chatHistory.length > 0) {
  //     saveToIDB(jobId, chatHistory);
  //   }
  // }, [chatHistory, jobId]);

  // Clear IDB data when interview is complete
  useEffect(() => {
    if (isComplete) {
      const clearSavedChat = async () => {
        try {
          const db = await initDB();
          const transaction = db.transaction(["probes"], "readwrite");
          const store = transaction.objectStore("probes");
          await store.delete(jobId);
        } catch (error) {
          console.error("Error clearing saved chat:", error);
        }
      };
      clearSavedChat();
    }
  }, [isComplete, jobId]);

  // Add this function to handle ending the call
  const handleEndCall = useCallback(() => {
    // Stop full interview recording
    stopFullInterviewRecording();
    
    // Stop the avatar stream
    stopAvatarStream();
    
    // Stop microphone if active
    if (window.microphoneStream) {
      window.microphoneStream.getTracks().forEach(track => track.stop());
    }
    
    // Stop any active recording
    recordingStateRef.current = false;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    // Reset states
    setIsListening(false);
    setCurrentTranscription('');
    setTextInput('');
    
    // Set complete state
    setIsComplete(true);
  }, [stopAvatarStream, stopFullInterviewRecording]);

  // Format elapsed time as MM:SS
  const formatTime = useCallback((milliseconds: number): string => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, []);

  // Session timer - tracks total interview time
  useEffect(() => {
    if (!sessionStartTime) return;
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - sessionStartTime;
      setSessionElapsedTime(elapsed);
      
      // Show warning at 12 minutes
      if (elapsed >= CONSTANTS.SESSION_WARNING_TIME && !hasShownWarning) {
        setShowTimeWarning(true);
        setHasShownWarning(true);
      }
      
      // Auto-submit at 15 minutes
      if (elapsed >= CONSTANTS.SESSION_MAX_TIME) {
        clearInterval(interval);
        
        // Stop recording and submit
        setIsListening(false);
        
        // If there's any current transcription, submit it
        if (currentTranscription || textInput) {
          setTimeout(() => {
            handleAnswerSubmission(textInput || currentTranscription);
          }, 500);
        } else {
          // Force completion even without text - just stop the interview
          setShowTimeWarning(false);
          stopFullInterviewRecording();
          handleEndCall();
        }
      }
    }, 1000); // Update every second
    
    return () => clearInterval(interval);
  }, [sessionStartTime, hasShownWarning, currentTranscription, textInput, handleAnswerSubmission, handleEndCall, stopFullInterviewRecording]);

  // Handle isLoading state - modify to show the welcome screen with preparation indicators
  if (isLoading || isPreparing) {
    // If we're at the welcome screen, show the welcome screen with preparation indicators
    if (currentQuestionIndex === -1) {
      // Return the same welcome screen but with disabled button and loading indicators
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-2xl w-full space-y-8">
            <div className="space-y-4">
              <h1 className="text-4xl font-bold tracking-tight">
                Ready for Your Interview! 🎙️
              </h1>
              <p className="text-xl text-muted-foreground">
                You'll be interviewed by our AI assistant who will ask you questions.
                Please respond by typing your answers clearly.
              </p>

              {/* Information badge about timings */}
              <div className="mt-3 p-4 rounded-lg border bg-slate-50">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                  <span className="text-[14px] font-semibold text-slate-800">Important timings</span>
                </div>
                <div className="overflow-hidden rounded-md border bg-white">
                  <table className="w-full text-[14px]">
                    <tbody>
                      <tr className="border-b">
                        <td className="px-3 py-2 text-slate-600">Total interview time</td>
                        <td className="px-3 py-2 font-medium text-slate-800 text-right">15 minutes</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 text-slate-600">Answer window</td>
                        <td className="px-3 py-2 font-medium text-slate-800 text-right">15 seconds</td>
                      </tr>
                      <tr className="border-b">
                        <td className="px-3 py-2 text-slate-600">Auto-submit after silence</td>
                        <td className="px-3 py-2 font-medium text-slate-800 text-right">3 seconds</td>
                      </tr>
                      <tr>
                        <td className="px-3 py-2 text-slate-600">No response timeout</td>
                        <td className="px-3 py-2 font-medium text-slate-800 text-right">15 seconds</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* Microphone check section (compact) */}
              <div className="mt-4 p-3 border rounded-lg bg-muted/30">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${microphoneActive ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <div>
                      <h2 className="text-[14px] font-semibold">Microphone</h2>
                      <p className="text-[14px] text-muted-foreground">
                        {microphoneActive ? 'Working properly' : 'Allow access to continue'}
                      </p>
                    </div>
                  </div>
                  {!microphoneActive && (
                    <Button onClick={checkMicrophone} variant="outline" size="sm">
                      Enable
                    </Button>
                  )}
                </div>
                {/* Preparation status (compact) */}
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${isInitialized ? 'bg-green-500' : isInitializing ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`}></div>
                    <p className="text-[14px] text-muted-foreground">{isInitialized ? 'Avatar ready' : isInitializing ? 'Initializing avatar…' : 'Init avatar'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${isStreaming ? 'bg-green-500' : isInitialized && !isStreaming ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`}></div>
                    <p className="text-[14px] text-muted-foreground">{isStreaming ? 'Stream ready' : isInitialized && !isStreaming ? 'Preparing stream…' : 'Waiting stream'}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`w-2.5 h-2.5 rounded-full ${sessionId ? 'bg-green-500' : isStreaming && !sessionId ? 'bg-yellow-500 animate-pulse' : 'bg-gray-300'}`}></div>
                    <p className="text-[14px] text-muted-foreground">{sessionId ? 'Session ready' : isStreaming && !sessionId ? 'Creating session…' : 'Waiting session'}</p>
                  </div>
                </div>
              </div>
              
              {/* Response Mode (accordion) */}
              <details className="mt-4 border rounded-lg bg-muted/30">
                <summary className="cursor-pointer px-4 py-2 text-[14px] font-semibold select-none">Response Mode</summary>
                <div className="p-4 pt-2 space-y-3">
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="auto-send"
                      name="send-mode"
                      checked={autoSendMode}
                      onChange={() => setAutoSendMode(true)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <label htmlFor="auto-send" className="text-[14px]">
                      <span className="font-medium">Auto Send</span>
                      <span className="text-muted-foreground block text-[14px]">Automatically send after 3s of silence</span>
                    </label>
                  </div>
                  <div className="flex items-center space-x-3">
                    <input
                      type="radio"
                      id="manual-send"
                      name="send-mode"
                      checked={!autoSendMode}
                      onChange={() => setAutoSendMode(false)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <label htmlFor="manual-send" className="text-[14px]">
                      <span className="font-medium">Manual Send</span>
                      <span className="text-muted-foreground block text-[14px]">Click "Done Talking" when finished</span>
                    </label>
                  </div>
                </div>
              </details>
              
              <p className="text-muted-foreground mt-4">
                This interview will use your microphone to analyze your responses. Find a quiet place
                and make sure you're ready to engage in a professional conversation.
              </p>
              
              {/* Hidden API key field that can be revealed with Ctrl+Shift+A */}
              {showApiKeyField && (
                <div className="mt-3 p-4 border border-blue-300 rounded-lg bg-blue-50">
                  <h3 className="text-[14px] font-semibold text-blue-800 mb-2">Admin Settings</h3>
                  <div className="space-y-2">
                    <label htmlFor="custom-api-key" className="block text-[14px] text-blue-800">
                      Custom Heygen API Key:
                    </label>
                    <input
                      type="text"
                      id="custom-api-key"
                      value={localApiKey}
                      onChange={(e) => setLocalApiKey(e.target.value)}
                      className="w-full p-2 border border-blue-300 rounded text-[14px]"
                      placeholder="Enter custom API key"
                    />
                    <p className="text-[14px] text-blue-600">
                      This key will override the default API key for this session only.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Always include the hidden input for the API key value */}
              <input 
                type="hidden" 
                id="heygen-custom-api-key" 
                name="heygen-custom-api-key" 
                value={localApiKey} 
              />
            </div>
            <Button 
              onClick={startInterview} 
              size="lg" 
              className="w-full bg-[#164a7a] hover:bg-[#2d6dad] text-white"
              disabled={!microphoneActive || !(isInitialized && isStreaming && sessionId)}
            >
              {isInitialized && isStreaming && sessionId ? (
                <>Begin Interview <ArrowRight className="ml-2 h-4 w-4" /></>
              ) : (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Preparing Interview...
                </div>
              )}
            </Button>
          </div>
        </div>
      );
    }
    
    // If we have a media stream but are still loading (waiting for the avatar to speak),
    // show the video with a loading overlay
    if (mediaStream) {
      return (
        <div className="min-h-screen flex flex-col bg-background">
          <div className="fixed top-0 left-0 w-full h-2 bg-gray-200">
            <div
              className="h-full bg-[#164a7a] transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          {/* Show the video in the background */}
          <div className="fixed inset-0 w-full h-full">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover"
            >
              <track kind="captions" />
            </video>
          </div>
          
          {/* Semi-transparent loading overlay */}
          <div className="fixed inset-0 bg-black/30 flex items-center justify-center">
            <div className="bg-white/90 p-8 rounded-lg shadow-lg max-w-md">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 border-4 border-t-blue-500 border-b-blue-500 border-l-transparent border-r-transparent rounded-full animate-spin"></div>
                <h2 className="text-xl font-semibold text-center">Preparing Interview</h2>
                <p className="text-gray-600 text-center">The AI interviewer is getting ready to speak...</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // If we're still in the initial loading state with no media stream yet,
    // show a simplified loading indicator on the welcome screen
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-8">
          <div className="space-y-4">
            <h1 className="text-4xl font-bold tracking-tight">
              Ready for Your Interview! 🎙️
            </h1>
            <p className="text-xl text-muted-foreground">
              You'll be interviewed by our AI assistant who will ask you questions.
              Please respond by typing your answers clearly.
            </p>
            
            {/* Loading indicator */}
            <div className="mt-8 p-6 border rounded-lg bg-muted/30">
              <div className="flex flex-col items-center space-y-4">
                <div className="w-16 h-16 border-4 border-t-blue-500 border-b-blue-500 border-l-transparent border-r-transparent rounded-full animate-spin"></div>
                <h2 className="text-xl font-semibold text-center">Initializing</h2>
                <p className="text-gray-600 text-center">Setting up your interview experience...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isComplete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-2xl w-full space-y-8">
          <div className="space-y-4 text-center">
            <CheckCircle className="mx-auto h-16 w-16 text-green-500" />
            <h1 className="text-4xl font-bold tracking-tight">Thank You!</h1>
            {countdown !== null && (
              <div className="relative w-24 h-24 mx-auto my-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-full h-full border-4 border-[#164a7a] rounded-full animate-[spin_3s_linear_infinite]" />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-bold text-[#164a7a]">
                    {countdown}
                  </span>
                </div>
              </div>
            )}
            <p className="text-xl text-muted-foreground">
              Your interview is complete.
            </p>
            <p className="text-muted-foreground">
              We appreciate your time and thoughtful responses. Our team will
              carefully review your answers and get back to you soon with the
              next steps in the process.
            </p>
            <p className="text-muted-foreground">
              If you have any questions or need to provide additional
              information, please don&apos;t hesitate to contact our HR
              department.
            </p>
            <p className="text-muted-foreground">
              Best of luck with your application!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const currentMessage = chatHistory[chatHistory.length - 1];
  const currentQuestionText = (() => {
    if (currentMessage?.role === "assistant") {
      try {
        const parsedContent = JSON.parse(currentMessage.content);
        return parsedContent.response;
      } catch {
        return currentMessage.content;
      }
    }
    return "";
  })();

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full h-full">
          {/* Avatar video display */}
          <div className="fixed inset-0 w-full h-full">
            {mediaStream ? (
              <div className="relative w-full h-full">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  className="w-full h-full object-cover"
                >
                  <track kind="captions" />
                </video>
                
                {/* Timer and warning notification */}
                <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-50">
                  <div className="flex items-center gap-2">
                    {/* Session timer */}
                    {sessionStartTime && (
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-full border ${
                        showTimeWarning 
                          ? 'bg-red-50 border-red-300 text-red-700' 
                          : sessionElapsedTime > CONSTANTS.SESSION_WARNING_TIME * 0.8 
                          ? 'bg-amber-50 border-amber-300 text-amber-700'
                          : 'bg-slate-100/90 border-slate-300 text-slate-700'
                      }`}>
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="text-sm font-medium">
                          {formatTime(sessionElapsedTime)}
                        </span>
                      </div>
                    )}
                  </div>
                  
                  {/* Warning notification */}
                  {showTimeWarning && (
                    <div className="flex items-center gap-3 px-4 py-2 rounded-lg bg-red-50 border border-red-300 shadow-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                        <span className="text-sm font-medium text-red-700">
                          3 minutes remaining
                        </span>
                      </div>
                      <button
                        onClick={() => setShowTimeWarning(false)}
                        className="text-red-600 hover:text-red-800"
                        aria-label="Dismiss warning"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Stream control buttons */}
                {/* <div className="absolute top-8 right-8 flex items-center space-x-3">
                  <Button 
                    onClick={startAvatarStream}
                    disabled={isInitializing || isStreaming}
                    variant="outline"
                    className="bg-blue-500 hover:bg-blue-600 text-white border-none"
                  >
                    {isInitializing ? 'Initializing...' : 'Start Stream'}
                  </Button>
                  
                  <Button 
                    onClick={stopAvatarStream}
                    disabled={!isStreaming}
                    variant="outline"
                    className="bg-red-500 hover:bg-red-600 text-white border-none"
                  >
                    Stop Stream
                  </Button>
                </div> */}
                
                {/* Captions/subtitles overlay */}
                {/* <div className="absolute bottom-32 left-0 right-0 flex justify-center">
                  <div className="bg-black/70 text-white px-6 py-3 rounded-lg max-w-2xl text-center text-lg">
                    {isSubmitting ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Processing your answer...</span>
                      </div>
                    ) : (
                      currentQuestionText
                    )}
                  </div>
                </div> */}
                
                {/* Avatar status indicator */}
                {/* <div className="absolute top-8 left-8 flex items-center space-x-2 bg-black/50 text-white px-4 py-2 rounded-full">
                  <div className={`w-3 h-3 rounded-full ${
                    isAvatarSpeaking ? 'bg-blue-500 animate-pulse' :
                    isListening && !isMuted ? 'bg-green-500 animate-pulse' : 
                    isSubmitting ? 'bg-orange-500 animate-pulse' : 
                    isMuted ? 'bg-red-500' :
                    'bg-yellow-500'
                  }`}></div>
                  <span>{
                    isAvatarSpeaking ? 'Avatar Speaking' :
                    isListening && !isMuted ? 'Listening' : 
                    isSubmitting ? 'Processing' : 
                    isMuted ? 'Muted' :
                    'Ready'
                  }</span>
                </div> */}
                
                {/* Answer window timer - shows after avatar finishes speaking */}
                {answerWindowActive && !isSubmitting && (
                  <div className={`absolute bottom-32 left-0 right-0 flex justify-center z-50 ${
                    answerWindowRemaining <= 5000 
                      ? 'bg-red-50 border-red-300 text-red-700'
                      : answerWindowRemaining <= 10000
                      ? 'bg-orange-50 border-orange-300 text-orange-700'
                      : 'bg-blue-50 border-blue-300 text-blue-700'
                  }`}>
                    <div className="flex items-center gap-2 text-sm px-4 py-2 rounded-full shadow-sm border">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="font-medium">
                        Answer time: {Math.ceil(answerWindowRemaining / 1000)}s
                      </span>
                    </div>
                  </div>
                )}
                
                {/* Control buttons */}
                <div className="absolute bottom-8 left-0 right-0 flex justify-center space-x-6">
                  {/* Show Done Talking button only in manual mode */}
                  {!autoSendMode && (
                    <button
                      type="button"
                      onClick={() => handleAnswerSubmission(textInput)}
                      disabled={!isListening || isSubmitting}
                      className={`inline-flex h-14 w-14 items-center justify-center rounded-full border shadow-sm hover:bg-opacity-80 active:scale-95 transition ${
                        isSubmitting 
                          ? "border-orange-200 bg-orange-100 text-orange-600 cursor-not-allowed" 
                          : !isListening
                            ? "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                            : "border-blue-200 bg-blue-600 text-white hover:bg-blue-700"
                      }`}
                      aria-label="Done Talking"
                      title={isSubmitting ? "Processing..." : "Done Talking"}
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-orange-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                        </svg>
                      )}
                    </button>
                  )}
                  
                  {/* Mute button */}
                  <button
                    type="button"
                    onClick={toggleMute}
                    className={`inline-flex h-14 w-14 items-center justify-center rounded-full border shadow-sm hover:bg-opacity-80 active:scale-95 transition ${
                      isMuted || isAvatarSpeaking
                        ? "border-red-200 bg-red-100 text-red-600" 
                        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                    }`}
                    aria-pressed={isMuted || isAvatarSpeaking}
                    disabled={isAvatarSpeaking}
                  >
                    {isMuted || isAvatarSpeaking ? (
                      <MicOff className="h-5 w-5 text-rose-600" />
                    ) : (
                      <Mic className="h-5 w-5 text-slate-700" />
                    )}
                  </button>
                  
                  {/* <Button 
                    onClick={handleEndCall}
                    variant="destructive"
                    className="bg-red-600 hover:bg-red-700 text-white px-6"
                    disabled={isSubmitting}
                  >
                    End Call
                  </Button> */}
                </div>
                
                {/* Add a transcription display - only show when showTranscriptionBox is true */}
                {isListening && textInput && !isSubmitting && showTranscriptionBox && (
                  <div className="absolute bottom-24 left-0 right-0 flex justify-center">
                    <div className="bg-black/70 text-white px-6 py-3 rounded-lg max-w-2xl text-center text-sm max-h-32 overflow-y-auto">
                      <p className="font-semibold mb-1">Current Transcription:</p>
                      <p>{textInput}</p>
                    </div>
                  </div>
                )}
                
                {/* Audio level indicator - keep this visible regardless of transcription box */}
                {microphoneActive && isListening && !isSubmitting && (
                  <div className="absolute bottom-24 left-0 right-0 flex justify-center">
                    <div className="bg-black/30 rounded-full h-4 w-64">
                      <div 
                        className={`h-full rounded-full transition-all duration-100 ${
                          isMuted ? 'bg-red-500' : 'bg-green-500'
                        }`}
                        style={{ width: `${isMuted ? 0 : audioLevel}%` }}
                      ></div>
                    </div>
                  </div>
                )}
                
                {/* Auto mode indicators */}
                {/* {autoSendMode && silenceCountdown > 0 && isListening && !isSubmitting && (
                  <div className="absolute bottom-32 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 text-sm text-white bg-black/50 px-4 py-2 rounded-full">
                      <div className={`w-2 h-2 rounded-full animate-pulse ${silenceCountdown <= 1 ? 'bg-red-500' : 'bg-orange-400'}`}></div>
                      <span>Auto-sending in: {silenceCountdown}s</span>
                    </div>
                  </div>
                )} */}
                
                {/* Manual mode indicator */}
                {!autoSendMode && isListening && textInput && textInput.trim() && !isSubmitting && (
                  <div className="absolute bottom-32 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 text-sm text-white bg-black/50 px-4 py-2 rounded-full">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      <span>Manual mode - Click "Done Talking" when ready</span>
                    </div>
                  </div>
                )}
                
                {/* Muted indicator */}
                {/* {isMuted && (
                  <div className="absolute bottom-32 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 text-sm text-white bg-red-500/80 px-4 py-2 rounded-full">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span>Microphone muted</span>
                    </div>
                  </div>
                )} */}
                
                {/* Avatar speaking indicator */}
                {/* {isAvatarSpeaking && (
                  <div className="absolute bottom-32 left-0 right-0 flex justify-center">
                    <div className="flex items-center gap-2 text-sm text-white bg-blue-500/80 px-4 py-2 rounded-full">
                      <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
                      <span>Avatar is speaking...</span>
                    </div>
                  </div>
                )} */}
                
                {/* Debug Panel */}
                {/* <div className="absolute top-8 right-8 bg-black/80 text-white p-4 rounded-lg text-xs max-w-xs">
                  <h3 className="font-bold mb-2 text-yellow-400">Debug Panel</h3>
                  <div className="space-y-1">
                    <div>Audio Level: <span className="text-green-400">{audioLevel}</span></div>
                    <div>Silence Threshold: <span className="text-blue-400">4</span></div>
                    <div>User Talked After AI: <span className={userTalkedAfterAI ? "text-green-400" : "text-red-400"}>{userTalkedAfterAI ? "Yes" : "No"}</span></div>
                    <div>Is User Stopped Talking: <span className={isUserStoppedTalking ? "text-red-400" : "text-green-400"}>{isUserStoppedTalking ? "Yes" : "No"}</span></div>
                    <div>Is Listening: <span className={isListening ? "text-green-400" : "text-red-400"}>{isListening ? "Yes" : "No"}</span></div>
                    <div>Is Muted: <span className={isMuted ? "text-red-400" : "text-green-400"}>{isMuted ? "Yes" : "No"}</span></div>
                    <div>Avatar Speaking: <span className={isAvatarSpeaking ? "text-red-400" : "text-green-400"}>{isAvatarSpeaking ? "Yes" : "No"}</span></div>
                    <div>Auto Send Mode: <span className={autoSendMode ? "text-green-400" : "text-blue-400"}>{autoSendMode ? "Auto" : "Manual"}</span></div>
                    <div>Silence Countdown: <span className="text-orange-400">{silenceCountdown}s</span></div>
                    <div>Last Audio Time: <span className="text-gray-400">{lastAudioTime ? new Date(lastAudioTime).toLocaleTimeString() : "None"}</span></div>
                    <div>Time Since Last Audio: <span className="text-purple-400">{lastAudioTime ? Math.round((Date.now() - lastAudioTime) / 1000) : 0}s</span></div>
                    <div>Silence Start Time: <span className="text-gray-400">{silenceStartTime ? new Date(silenceStartTime).toLocaleTimeString() : "None"}</span></div>
                    <div>Silence Duration: <span className="text-yellow-400">{silenceStartTime ? Math.round((Date.now() - silenceStartTime) / 1000) : 0}s</span></div>
                    <div>Is Submitting: <span className={isSubmitting ? "text-red-400" : "text-green-400"}>{isSubmitting ? "Yes" : "No"}</span></div>
                    <div>Text Input Length: <span className="text-cyan-400">{textInput?.length || 0}</span></div>
                    <div>Current Transcription: <span className="text-cyan-400">{currentTranscription?.length || 0}</span></div>
                  </div>
                </div> */}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full">
                <p className="text-gray-500 mb-4">Avatar will appear here</p>
                <Button 
                  onClick={startAvatarStream}
                  disabled={isInitializing}
                  className="bg-blue-500 hover:bg-blue-600 text-white"
                >
                  {isInitializing ? 'Initializing...' : 'Start Stream'}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
